import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CommercialStatusBadge,
  CommercialTypeBadge,
} from '@/modules/commercial/components';
import { useCommercialOperations, useClients } from '@/modules/commercial/hooks';
import {
  COMMERCIAL_OPERATION_STATUS_LABELS,
  COMMERCIAL_OPERATION_TYPE_LABELS,
  type CommercialOperationStatus,
  type CommercialOperationType,
} from '@/modules/commercial/types';
import { filterCommercialOperations } from '@/modules/commercial/utils/operationFilters';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

export function OperationsListPage() {
  const { operations, loading, error } = useCommercialOperations();
  const { clients } = useClients();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('commercial.write');

  const [statusFilter, setStatusFilter] = useState<
    CommercialOperationStatus | 'all'
  >('all');
  const [typeFilter, setTypeFilter] = useState<CommercialOperationType | 'all'>(
    'all',
  );
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      filterCommercialOperations(operations, {
        status: statusFilter,
        type: typeFilter,
        search,
      }),
    [operations, statusFilter, typeFilter, search],
  );

  const clientName = (id: string | null) =>
    id ? (clients.find((c) => c.id === id)?.name ?? '—') : '—';

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Operaciones comerciales</h1>
        {canWrite ? (
          <Link
            to={`${modulePaths.commercial}/operaciones/nueva`}
            className="auth-button inventory-page-header__btn"
          >
            Nueva operación
          </Link>
        ) : null}
      </div>

      <div className="reservation-filters users-form-grid">
        <label className="auth-label">
          Estado
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CommercialOperationStatus | 'all')
            }
          >
            <option value="all">Todos</option>
            {(Object.keys(COMMERCIAL_OPERATION_STATUS_LABELS) as CommercialOperationStatus[]).map(
              (s) => (
                <option key={s} value={s}>
                  {COMMERCIAL_OPERATION_STATUS_LABELS[s]}
                </option>
              ),
            )}
          </select>
        </label>
        <label className="auth-label">
          Tipo
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as CommercialOperationType | 'all')
            }
          >
            <option value="all">Todos</option>
            {(Object.keys(COMMERCIAL_OPERATION_TYPE_LABELS) as CommercialOperationType[]).map(
              (t) => (
                <option key={t} value={t}>
                  {COMMERCIAL_OPERATION_TYPE_LABELS[t]}
                </option>
              ),
            )}
          </select>
        </label>
        <label className="auth-label">
          Buscar
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Título"
          />
        </label>
      </div>

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      {!loading && filtered.length === 0 ? (
        <p className="auth-muted">No hay operaciones con los filtros aplicados.</p>
      ) : null}

      {!loading && filtered.length > 0 ? (
        <table className="users-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Cliente</th>
              <th>Total</th>
              <th>Ítems</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((op) => (
              <tr key={op.id}>
                <td>{op.title}</td>
                <td>
                  <CommercialTypeBadge type={op.type} />
                </td>
                <td>
                  <CommercialStatusBadge status={op.status} />
                </td>
                <td>{clientName(op.clientId)}</td>
                <td>
                  {op.total > 0 ? `$ ${op.total.toLocaleString('es-AR')}` : '—'}
                </td>
                <td>{op.inventoryItemIds.length}</td>
                <td>
                  <Link
                    to={`${modulePaths.commercial}/operaciones/${op.id}`}
                    className="link-button"
                  >
                    Ver
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
